import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { ArrowRight, Menu, X, MessageCircle } from 'lucide-react';
import VibhraadMainLogo from '../../vibhlogo.png';

const NAV_LINKS = [
  { to: '/about',      label: 'About' },
  { to: '/services',   label: 'Services' },
  { to: '/careers',    label: 'Careers' },
  { to: '/leadership', label: 'Leadership' },
  { to: '/contact',    label: 'Contact' },
];

export const Navigation = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // Close mobile menu on resize
  useEffect(() => {
    const fn = () => { if (window.innerWidth >= 768) setOpen(false); };
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100 py-3">
      <div className="max-w-7xl mx-auto px-5 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 flex-shrink-0">
          <img src={VibhraadMainLogo} alt="Vibhraad Technology" className="h-12 w-auto" loading="lazy" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1">
          {NAV_LINKS.map(l => (
            <Link key={l.to} to={l.to}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-colors">
              {l.label}
            </Link>
          ))}
        </nav>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-3">
          <a href="https://wa.me/7004535563" target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-green-600 transition-colors">
            <MessageCircle className="w-4 h-4" /> WhatsApp
          </a>
          <button
            onClick={() => navigate('/contact')}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-all shadow-lg shadow-blue-600/20">
            Get a Quote <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* Mobile hamburger */}
        <button onClick={() => setOpen(o => !o)} className="md:hidden text-gray-800 p-1">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden overflow-hidden bg-white border-t border-gray-100 shadow-xl">
            <div className="flex flex-col px-5 py-4 gap-1">
              {NAV_LINKS.map(l => (
                <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
                  className="px-4 py-3 rounded-xl text-sm font-semibold text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors">
                  {l.label}
                </Link>
              ))}
              <a href="https://wa.me/7004535563" target="_blank" rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="px-4 py-3 rounded-xl text-sm font-medium text-gray-700 hover:bg-green-50 hover:text-green-600 transition-colors">
                WhatsApp: +91 7004535563
              </a>
              <Link to="/contact" onClick={() => setOpen(false)}
                className="mt-2 bg-blue-600 text-white text-sm font-semibold px-4 py-3 rounded-xl text-center hover:bg-blue-500 transition-colors">
                Get a Quote
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-gray-950 text-white">
      <div className="max-w-7xl mx-auto px-6 pt-16 pb-8">

        {/* Top CTA Strip */}
        <div className="bg-blue-600 rounded-2xl px-8 py-6 mb-14 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <p className="text-white font-black text-xl mb-1">Ready to Build Something Great?</p>
            <p className="text-blue-100 text-sm">Get a free project estimate — we respond within 24 hours.</p>
          </div>
          <Link to="/contact" className="flex-shrink-0 bg-white text-blue-600 font-bold text-sm px-6 py-3 rounded-xl hover:bg-blue-50 transition-colors">
            Get Free Quote →
          </Link>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-14">

          {/* Col 1: Brand */}
          <div className="col-span-2 md:col-span-1">
            <p className="text-white font-black text-lg mb-1">Vibhraad Technology</p>
            <p className="text-blue-500 text-xs font-mono mb-4">Private Limited</p>
            <p className="text-gray-400 text-sm leading-relaxed mb-5">
              Enterprise software built for scale. Trusted by businesses across 25+ countries.
            </p>
            <div className="flex gap-3">
              {socialLinks.map((s, i) => (
                <a key={i} href={s.href} target="_blank" rel="noopener noreferrer"
                  className="w-9 h-9 rounded-lg bg-white/8 hover:bg-blue-600 flex items-center justify-center transition-colors">
                  {s.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Col 2: Services */}
          <div>
            <p className="text-white font-bold text-sm mb-4 uppercase tracking-wider">Services</p>
            <ul className="space-y-2.5">
              {services.map((s, i) => (
                <li key={i}>
                  <Link to={s.path} className="text-gray-400 hover:text-blue-400 text-sm transition-colors">{s.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3: Locations */}
          <div>
            <p className="text-white font-bold text-sm mb-4 uppercase tracking-wider">Locations</p>
            <ul className="space-y-2.5">
              {serviceLocations.map((l, i) => (
                <li key={i}>
                  <Link to={l.path} className="text-gray-400 hover:text-blue-400 text-sm transition-colors">{l.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 4: Company */}
          <div>
            <p className="text-white font-bold text-sm mb-4 uppercase tracking-wider">Company</p>
            <ul className="space-y-2.5 mb-6">
              {[
                { label: 'About Us', to: '/about' },
                { label: 'Leadership', to: '/leadership' },
                { label: 'Careers', to: '/careers' },
                { label: 'Pricing', to: '/pricing' },
                { label: 'Contact', to: '/contact' },
                { label: 'Blog', to: '/blog' },
              ].map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-gray-400 hover:text-blue-400 text-sm transition-colors">{l.label}</Link>
                </li>
              ))}
            </ul>
            <p className="text-white font-bold text-sm mb-3 uppercase tracking-wider">Contact</p>
            <p className="text-gray-400 text-sm mb-1">vibhraad@gmail.com</p>
            <p className="text-gray-500 text-xs">F-375, Sector-36, Noida</p>
            <p className="text-gray-500 text-xs">Uttar Pradesh 201301, India</p>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/8 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">© 2025–2026 Vibhraad Technology Pvt Ltd · CIN: U62091UP2025PTC234668 · <span className="text-gray-600">v1.0.0</span></p>
          <div className="flex gap-5 text-sm">
            <Link to="/privacy" className="text-gray-500 hover:text-gray-300 transition-colors">Privacy</Link>
            <Link to="/terms" className="text-gray-500 hover:text-gray-300 transition-colors">Terms</Link>
            <Link to="/cookies" className="text-gray-500 hover:text-gray-300 transition-colors">Cookies</Link>
          </div>
        </div>

      </div>
    </footer>
  );
};

// Social Media Links
const socialLinks = [
    {
        href: 'https://www.linkedin.com/company/vibhraad/',
        icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
        </svg>
    },
    {
        href: 'https://facebook.com/Vibhraad.in',
        icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385h-3.047v-3.47h3.047v-2.642c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953h-1.514c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385c5.737-.9 10.125-5.864 10.125-11.854z" />
        </svg>
    }
];

// Updated Services for Footer - now includes the new service pages
const services = [
    { name: 'Custom Software Development', path: '/enterprise-solutions' },
    { name: 'Web Development', path: '/services' },
    { name: 'Mobile App Development', path: '/mobile-app-development' },
    { name: 'E-commerce Development', path: '/ecommerce-development' },
    { name: 'Digital Marketing', path: '/digital-marketing' },
    { name: 'UI/UX Design', path: '/services/india/design' },
    
];

// Service Locations for Footer
const serviceLocations = [
    { name: 'India', path: '/india' },
    { name: 'Bangalore', path: '/bangalore' },
    { name: 'Hyderabad', path: '/hyderabad' },
    { name: 'Gurgaon', path: '/gurgaon' },
    { name: 'New Delhi', path: '/delhi' },
    { name: 'Lucknow', path: '/lucknow' },
    { name: 'Gorakhpur', path: '/gorakhpur' }
];

// Updated service categories - removed influencer references, added new service pages
const serviceCategories = {
    'Development Services': [
        { name: 'Custom Software Development', path: '/enterprise-solutions' },
        { name: 'Web Development', path: '/services' },
        { name: 'Mobile App Development', path: '/mobile-app-development' },
        { name: 'E-commerce Development', path: '/ecommerce-development' },
        { name: 'Food Delivery Solutions', path: '/services/india/food-delivery' }
    ],
    'Business Solutions': [
        { name: 'Enterprise Software', path: '/enterprise-solutions' },
        { name: 'Hotel Booking Systems', path: '/services/india/hotel-booking' },
        { name: 'Pharmacy Management', path: '/services/india/pharmacy' },
        { name: 'Inventory Management', path: '/services/india/inventory' },
        { name: 'CRM Solutions', path: '/services/india/crm' },
        { name: 'Point of Sale Systems', path: '/services/india/pos' },
        { name: 'Accounting Software', path: '/services/india/accounting' },
        { name: 'Supply Chain Management', path: '/services/india/supply-chain' },
        { name: 'Human Resource Management', path: '/services/india/hrm' },
        { name: 'Enterprise Resource Planning', path: '/services/india/erp' },
        { name: 'Online Appointment Booking', path: '/services/india/appointment-booking' },
        { name: 'Fleet Management Systems', path: '/services/india/fleet-management' },
        { name: 'Customer Support Platforms', path: '/services/india/customer-support' }
    ],
    'Consulting': [
        { name: 'Technical Consultation', path: '/services/india/technical' },
        { name: 'Project Management', path: '/services/india/project-management' }
    ],
    'Design Services': [
        { name: 'UI/UX Design', path: '/services/india/ui-ux' },
        { name: 'Graphic Design', path: '/services/india/graphic' },
        { name: 'Branding', path: '/services/india/branding' },
        { name: 'Product Design', path: '/services/india/product' }
    ],
    'Marketing Services': [
        { name: 'SEO Optimization', path: '/services/india/seo' },
        { name: 'Content Marketing', path: '/services/india/content' },
        { name: 'PPC Campaigns', path: '/services/india/ppc' },
        { name: 'Social Media Management', path: '/services/india/social-media' }
    ]
};

// Updated navigation items - removed influencer page, organized for business focus
const navItems = [
    {
        name: 'Services',
        path: '/services',
        categories: serviceCategories
    },
    {
        name: 'About',
        path: '/about'
    },
    {
        name: 'Pricing',
        path: '/pricing'
    },
    // {
    //     name: 'Projects',
    //     path: '/projects'
    // },
    {
        name: "Digital Marketing",
        path: "/digital-marketing",
        dropdown: [
            { name: "Facebook Ads Marketing", path: "/digital-marketing/facebook-ads" },
            { name: "Email Marketing", path: "/digital-marketing/email-marketing" },
            { name: "SEO Optimization", path: "/digital-marketing/seo-optimization" },
            { name: "Content Marketing", path: "/digital-marketing/content-marketing" },
            { name: "Google Ads Management", path: "/digital-marketing/google-ads" },
            { name: "Social Media Strategy", path: "/digital-marketing/social-media-strategy" },
            { name: "Video Marketing", path: "/digital-marketing/video-marketing" },
            { name: "Mobile Marketing", path: "/digital-marketing/mobile-marketing" },
            { name: "Web Analytics", path: "/digital-marketing/web-analytics" },
            { name: "Custom Marketing Solutions", path: "/digital-marketing/custom-solutions" },
        ]
    }
];